import React, { FC, useMemo, useState } from 'react';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  RoleGuard,
  Table,
  useTable,
} from 'portal-commons';
import { faUserCircle } from '@fortawesome/pro-regular-svg-icons';
import { loginsManagerController, User } from '@/apis/core';
import {
  UserCreatePayload,
  UserUpdatePayload,
  Modal,
  ModalType,
} from '../../types';
import { CspProfile } from '@/types/user';
import { toastFlashMessage } from '@/utils';
import SectionCard from './SectionCard';
import UserListingRow from './UserListingRow';
import UserEditModal from './UserEditModal';

const HeadRows = [
  { id: 'fullName', label: 'NAME', sortable: true },
  { id: 'email', label: 'EMAIL Address', sortable: false },
  { id: 'uid', label: 'USER ID', sortable: false },
  { id: 'role', label: 'ROLE', sortable: false },
  { id: 'created', label: 'CREATED ON', sortable: true },
  { id: 'lastLogin', label: 'LAST LOGIN', sortable: true },
  { id: 'action', label: '', sortable: false },
];
const PAGE_SIZE = 10;

interface Props {
  profile: CspProfile;
  users: User[];
  roles: string[];
  onUsersUpdate: (users: User[]) => void;
}

const TabUserManagement: FC<Props> = ({
  profile,
  users,
  roles,
  onUsersUpdate,
}) => {
  const maxPortalUsers = profile.maxPortalUsers || 10;
  const isCreateDisabled = users.length >= maxPortalUsers;
  const [processing, setProcessing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [modal, setModal] = useState<ModalType>(Modal.ADD);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);

  const table = useTable(
    async (filter) => {
      const newUsers =
        users.length > 0 ? users : (await getUsers(false, true)) ?? [];
      return {
        data: newUsers,
        total: newUsers.length,
        rowsPerPage: PAGE_SIZE,
        page: filter?.page || 1,
      };
    },
    {
      deps: [users],
      defaultFilter: {
        sortField: 'created',
        ascendingOrder: false,
      },
      inMemory: true,
    }
  );

  const getUsers = async (delay = false, initialLoad = false) => {
    // TCR-12401: Workaround for Okta update user delay
    delay &&
      (await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      }));

    const response = await loginsManagerController.getUsers();
    response.length > 0 && !initialLoad && onUsersUpdate(response);
    return response || [];
  };

  const handleAddUser = async (data: UserCreatePayload) => {
    const response = await loginsManagerController.createUser(data);
    if (response) {
      toastFlashMessage(
        `New User ${data.firstName} ${data.lastName} Created`,
        'success'
      );
    }
    return !!response;
  };

  const handleUpdateUser = async (data: UserUpdatePayload) => {
    if (!selectedUser) return false;

    const response = await loginsManagerController.updateUser(
      selectedUser.id,
      data
    );
    if (response) {
      toastFlashMessage(
        `User ${selectedUser.profile.fullName} Edit Successful`,
        'success'
      );
    }
    return !!response;
  };

  const handleDeleteUser = async (userToDelete: User) => {
    if (!userToDelete) return false;

    const response = await loginsManagerController.deleteUser(userToDelete.id);
    if (response) {
      toastFlashMessage(
        `User ${userToDelete.profile.fullName} Deleted`,
        'success'
      );
    }
    return !!response;
  };

  const handleModalSubmit = async (
    data: UserCreatePayload | UserUpdatePayload | User
  ) => {
    setProcessing(true);
    let success = false;
    const isEdit = modal === Modal.EDIT;

    try {
      switch (modal) {
        case Modal.ADD:
          success = await handleAddUser(data as UserCreatePayload);
          break;
        case Modal.EDIT:
          success = await handleUpdateUser(data as UserUpdatePayload);
          break;
        case Modal.DELETE:
          success = await handleDeleteUser(data as User);
          break;
        default:
          return;
      }

      if (success) {
        setIsOpen(false);
        await getUsers(isEdit);
      }
    } finally {
      setProcessing(false);
    }
  };

  const displayUsers = useMemo(() => {
    if (users.length === 0) return [];

    let tempData = users.slice();
    const { sortField, page: currPage, ascendingOrder } = table.filter;
    if (sortField) {
      tempData = tempData.sort((a, b) => {
        if (sortField === 'fullName') {
          const dataA = a.profile?.fullName || '';
          const dataB = b.profile?.fullName || '';
          return ascendingOrder
            ? dataA.localeCompare(dataB)
            : dataB.localeCompare(dataA);
        } else if (['created', 'lastLogin'].includes(sortField)) {
          const dataA = (a[sortField as keyof User] as number) || 0;
          const dataB = (b[sortField as keyof User] as number) || 0;
          return ascendingOrder ? dataA - dataB : dataB - dataA;
        } else {
          return 0;
        }
      });
    }
    if (tempData.length === users.length && tempData.length > PAGE_SIZE) {
      const start = (currPage - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      tempData = tempData.slice(start, end);
    }
    return tempData;
  }, [users, table.filter]);

  const onAddUser = () => {
    setIsOpen(true);
    setModal(Modal.ADD);
    setSelectedUser(undefined); // Clear any previously selected user
  };
  const onEdit = (user: User) => {
    setIsOpen(true);
    setModal(Modal.EDIT);
    setSelectedUser(user);
  };
  const onDelete = (user: User) => {
    setIsOpen(true);
    setModal(Modal.DELETE);
    setSelectedUser(user);
  };

  return (
    <Box data-testid="tabUserManagementContainer">
      <SectionCard
        icon={faUserCircle}
        title="Users"
        sx={{
          '& > div': {
            mb: 8,
          },
          '& button': {
            height: '32px',
            border: 'none',
          },
        }}
        customButton={
          <RoleGuard feature="profile.addUser">
            <Button
              shape="square"
              color="secondary"
              disabled={isCreateDisabled}
              onClick={onAddUser}
            >
              Add User
            </Button>
          </RoleGuard>
        }
        dataTestId="userManagementSection"
      >
        <Box sx={{ background: '#CCCFD0', height: 1, mb: 16 }} />
        <Table
          testId="usersTable"
          loading={table.loading || processing}
          headRows={HeadRows}
          disableHover
          emptyState="no users to view"
          filter={table.filter}
          tableData={displayUsers.map((user) => (
            <UserListingRow
              key={user.id}
              user={user}
              onEdit={() => onEdit(user)}
              onDelete={() => onDelete(user)}
            />
          ))}
          handleChangePage={table.handleChangePage}
          createSortHandler={table.handleSorting}
          pagination={table.pagination}
          emptyBlockHeight="160px"
        />
        {isCreateDisabled && (
          <Box
            sx={{
              mt: 'l',
              textAlign: 'center',
              fontSize: 's',
              fontWeight: 400,
            }}
            data-testid="maxUserLimitWarning"
          >
            Maximum number of users reached!
            <br />
            Please delete an existing user if you wish to create a new one.
          </Box>
        )}
      </SectionCard>
      {isOpen && (
        <UserEditModal
          open={isOpen}
          mode={modal}
          user={selectedUser}
          roles={roles}
          processing={processing}
          onClose={() => setIsOpen(false)}
          onSubmit={handleModalSubmit}
          dataTestId={`userEditModal_${modal}`}
        />
      )}
    </Box>
  );
};

export default TabUserManagement;
